import { Alert, Col, Collapse, Layout, Row, Space, Typography } from 'antd';
import { format, subDays, addDays } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  DerivedTeamRoster,
  FatigueViolation,
  UserFatigueViolationReport,
  UserShiftClashReport,
} from '../../../hooks/types';
import { useLoggedInUser } from '../../../hooks/useLoggedInUser';
import { useNotify } from '../../../hooks/useNotify';
import useQueryTeamById from '../../../hooks/useQueryTeamById';
import useQueryTeamRoster from '../../../hooks/useQueryTeamRoster';
import useQueryUserFatigueReport from '../../../hooks/useQueryUserFatigueReport';
import useQueryUserShiftClash from '../../../hooks/useQueryUserShiftClash';
import { onApolloError } from '../../../lib/graphql/helpers';
import { userFullName } from '../../../lib/utils';
import { shiftDateFormat } from '../../../lib/utils/date-time';
import HeaderSection from '../../HeaderSection';
import Roster from '../../Roster';
import RosterDatePicker from '../../Roster/RosterDatePicker';
import ShiftViolationDisplay from '../../Roster/ShiftViolationDisplay';
import { shiftRecords } from '../../Roster/lib/util';
import type {
  RosterDisplayMode,
  RosterTableRowRecord,
  RosterUser,
} from '../../Roster/types';
import { Optional } from '../../Shared';
import { archiveStatusSchema, recordStatusSchema } from '../types';
import FatigueGuide from './FatigueGuide';
import ShiftClashAlert from './ShiftClashAlert';
import UserFatigueAlert from './UserFatigueAlert';
import type { ShiftFatigueViolation, UserShiftFatigueViolation } from './types';

const { Panel } = Collapse;

const { Text } = Typography;

// Look back or forward specific days to determine fatigue violations.
// Choose 13 since we need to look back at least 13 days to determine if new shift is violated the rule.
// This is based on the day shift pattern: 5-1-5-2, work 5 consecutive days rest 1 day, then 5 consecutive days rest 2 days.
const fatigueCheckOffsetDays = 13;

// Buffer days to query shifts for the selected given date range. We use this to ensure all shifts within selected date range
// will be returned for rendering, e.g. overflow shifts that may start a day before from previous week date range.
// If the extra shifts can't fit on the roster, they won't be displayed.
const bufferShiftQueryDays = 1;

const getFatigueViolated = (violations: readonly FatigueViolation[]) =>
  violations
    .filter((violation) => violation.report.violated)
    .map((violation) => ({
      shifts: violation.report.shifts,
      shiftDate: format(new Date(violation.dateKey), shiftDateFormat),
    }));

const emptyUserShiftFatigueViolations: readonly UserShiftFatigueViolation[] =
  [];

/**
 * Renders a roster for a specific Team.
 */
const TeamRoster = ({ todayDate }: { readonly todayDate: Date }) => {
  const { notify } = useNotify();
  const [rosterDisplayMode, setRosterDisplayMode] =
    useState<RosterDisplayMode>('weekly');

  const [rosterDates, setRosterDates] = useState<readonly Date[]>([]);

  const { teamId } = useParams<{ readonly teamId: string | undefined }>();

  const { loggedInUser } = useLoggedInUser();

  const [currentTeamRoster, setCurrentTeamRoster] = useState<
    DerivedTeamRoster | undefined
  >();

  const [teamIon, setTeamION] = useState<string>();
  const [teamNotFound, setTeamNotFound] = useState(false);

  const firstRosterDate = rosterDates[0];
  const lastRosterDate = rosterDates[rosterDates.length - 1];

  const { refetch: refetchRosters, loading: loadingTeamRosterData } =
    useQueryTeamRoster(
      subDays(rosterDates[0] ?? todayDate, bufferShiftQueryDays),
      addDays(lastRosterDate ?? todayDate, bufferShiftQueryDays),
      onApolloError(notify.error),
      teamId,
      undefined,
      (teamRosters) => setCurrentTeamRoster(teamRosters[0])
    );

  useQueryTeamById(
    (teamData) => {
      setTeamION(teamData?.internal_order_number);
      setTeamNotFound(teamData === undefined);
    },
    undefined,
    teamId,
    teamIon !== undefined
  );

  const fatigueQueryStartDate = subDays(
    firstRosterDate ?? todayDate,
    fatigueCheckOffsetDays
  );

  const fatigueQueryEndDate = addDays(
    lastRosterDate ?? todayDate,
    fatigueCheckOffsetDays
  );

  const usersOnRoster: Record<string, RosterUser> | undefined =
    currentTeamRoster?.roleRosters.reduce((acc, roleRoster) => {
      const users = roleRoster.users.reduce((acc, user) => {
        if (user.historic) {
          return acc;
        }

        return {
          ...acc,
          [user.userId]: user,
        };
      }, {});

      return {
        ...acc,
        ...users,
      };
    }, {});

  const [usersFatigueReport, setUsersFatigueReport] =
    useState<readonly UserFatigueViolationReport[]>();

  const { refetch: refetchUserFatigueReport } = useQueryUserFatigueReport(
    Object.keys(usersOnRoster ?? {}),
    fatigueQueryStartDate,
    fatigueQueryEndDate,
    onApolloError(notify.error),
    setUsersFatigueReport
  );

  const { data: usersShiftClash, refetch: refetchUsersShiftClash } =
    useQueryUserShiftClash(
      Object.keys(usersOnRoster ?? {}),
      subDays(rosterDates[0] ?? todayDate, bufferShiftQueryDays),
      addDays(lastRosterDate ?? todayDate, bufferShiftQueryDays),
      onApolloError(notify.error)
    );

  useEffect(() => {
    void refetchRosters();
  }, [refetchRosters]);

  useEffect(() => {
    void refetchUserFatigueReport();
    void refetchUsersShiftClash();
  }, [refetchUserFatigueReport, refetchUsersShiftClash, usersOnRoster]);

  const teamRoster: readonly DerivedTeamRoster[] =
    currentTeamRoster === undefined ? [] : [currentTeamRoster];

  const updateRosterDates = useCallback(
    (dates: readonly Date[]) => setRosterDates(dates),
    []
  );

  const displayModeUpdated = useCallback(
    (displayMode: RosterDisplayMode) => setRosterDisplayMode(displayMode),
    []
  );

  const violations: readonly UserShiftFatigueViolation[] | undefined =
    usersFatigueReport
      ?.filter((userFatigue) =>
        [
          ...userFatigue.consecutiveShiftViolations,
          ...userFatigue.maxShiftHoursViolations,
          ...userFatigue.shiftRestGapViolations,
        ].find((violationReport) => violationReport.report.violated)
      )
      .reduce((acc, userFatigue) => {
        const rosterUser = usersOnRoster?.[userFatigue.userId];

        if (rosterUser === undefined) {
          return acc;
        }

        const consecutiveShiftViolations: readonly ShiftFatigueViolation[] =
          getFatigueViolated(userFatigue.consecutiveShiftViolations);

        const maxShiftHoursViolations: readonly ShiftFatigueViolation[] =
          getFatigueViolated(userFatigue.maxShiftHoursViolations);

        const shiftRestGapViolations: readonly ShiftFatigueViolation[] =
          getFatigueViolated(userFatigue.shiftRestGapViolations);

        return [
          ...acc,
          {
            userId: userFatigue.userId,
            userFullName: userFullName({
              first_name: rosterUser.firstName,
              last_name: rosterUser.lastName,
            }),
            consecutiveShiftViolations,
            maxShiftHoursViolations,
            shiftRestGapViolations,
          },
        ];
      }, emptyUserShiftFatigueViolations);

  const shiftClashes: readonly UserShiftClashReport[] | undefined =
    usersShiftClash?.queryShiftClashReport?.filter(
      (shiftClash) => shiftClash.clashedShiftGroups.length > 0
    );

  const noViolations = violations === undefined || violations.length === 0;

  const noShiftClash = shiftClashes === undefined || shiftClashes.length === 0;

  // Set roster table sticky header position top offset, so there is a room for sticky fatigue alerts
  const rosterTableHeaderOffset = noViolations ? 60 : noShiftClash ? 110 : 165;

  if (teamNotFound) {
    return (
      <Alert
        message={'You are not able to view this team.'}
        description={'Go back to All Teams and try again.'}
        type="error"
        showIcon
      />
    );
  }

  const rosterShiftsRecords: readonly RosterTableRowRecord[] =
    teamRoster[0] !== undefined ? shiftRecords(teamRoster[0], false, []) : [];

  return (
    <Layout>
      <Space
        direction="vertical"
        style={{
          position: 'sticky',
          top: 62,
          zIndex: 15,
          backgroundColor: '#f0f2f5',
        }}
      >
        <Optional show={!noShiftClash}>
          <Collapse destroyInactivePanel={true}>
            <Panel
              id="shift-clash-collapse-section"
              header={
                <Text>
                  <strong>Shift clashes</strong> You are viewing team&nbsp;
                  <strong>{currentTeamRoster?.teamName}</strong>
                </Text>
              }
              key="user-shift-clashes"
              style={{ backgroundColor: 'rgba(222, 20, 4, 0.3)' }}
            >
              {shiftClashes?.map((shiftClashReport) => (
                <ShiftClashAlert
                  key={`${shiftClashReport.userId}-shift-clashes`}
                  teamName={currentTeamRoster?.teamName ?? ''}
                  userShiftClashReport={shiftClashReport}
                  usersOnRoster={usersOnRoster}
                />
              ))}
            </Panel>
          </Collapse>
        </Optional>
        <Optional show={!noViolations}>
          <Collapse destroyInactivePanel={true}>
            <Panel
              id="shift-fatigue-collapse-section"
              header={
                <>
                  <strong>Fatigue Violations</strong> You are viewing team&nbsp;
                  <strong>{currentTeamRoster?.teamName}</strong>
                </>
              }
              key="user-fatigue-violations"
              style={{ backgroundColor: '#fffbe6' }}
            >
              <FatigueGuide />
              <Collapse destroyInactivePanel={true}>
                <Collapse.Panel
                  id="shift-fatigue-calendar-collapse-section"
                  header={
                    <>
                      <strong>Fatigue Violations</strong> calendar for&nbsp;
                      <strong>{currentTeamRoster?.teamName}</strong>
                    </>
                  }
                  key="user-fatigue-violations-calendar"
                  style={{ backgroundColor: '#fffbe6' }}
                >
                  <ShiftViolationDisplay
                    {...{
                      usersFatigueReport,
                      dayShiftPeriod: teamRoster[0]?.dayShiftPeriod,
                      rosterShiftsRecords,
                    }}
                  />
                </Collapse.Panel>
              </Collapse>
              <Collapse destroyInactivePanel={true}>
                <Collapse.Panel
                  id="shift-fatigue-list-collapse-section"
                  header={
                    <>
                      <strong>Fatigue Violations</strong> list&nbsp;
                      <strong>{currentTeamRoster?.teamName}</strong>
                    </>
                  }
                  key="user-fatigue-violations-list"
                  style={{ backgroundColor: '#fffbe6' }}
                >
                  {violations?.map((violation) => (
                    <UserFatigueAlert
                      key={`${violation.userId}-fatigue-violations`}
                      violation={violation}
                      teamName={currentTeamRoster?.teamName ?? ''}
                    />
                  ))}
                </Collapse.Panel>
              </Collapse>
            </Panel>
          </Collapse>
        </Optional>
      </Space>
      <HeaderSection
        teamId={teamId}
        ion={teamIon}
        title={`${currentTeamRoster?.teamName ?? ''} Roster ${
          currentTeamRoster?.teamRecordStatus ===
          recordStatusSchema.Values.Deleted
            ? `(${archiveStatusSchema.Values.Archived})`
            : ''
        }`}
      />
      <Row>
        <Col flex="auto">
          <Optional show={loggedInUser !== undefined}>
            <>
              <Col span={24}>
                <RosterDatePicker
                  datesUpdated={updateRosterDates}
                  displayModeUpdated={displayModeUpdated}
                />
                <Roster
                  tableHeaderOffset={rosterTableHeaderOffset}
                  teamRosters={teamRoster}
                  rosterDates={rosterDates}
                  rosterDisplayMode={rosterDisplayMode}
                  loading={loadingTeamRosterData}
                  allowExport={true}
                  readOnly={
                    currentTeamRoster?.teamRecordStatus ===
                    recordStatusSchema.Values.Deleted
                  }
                />
              </Col>
            </>
          </Optional>
        </Col>
      </Row>
    </Layout>
  );
};

export default TeamRoster;
